import type {
    BillingBlockReason,
    BillingBlockedPayload,
    BillingEntitlement,
    BillingThresholdState,
} from "~/features/billing/types";

interface SessionBillingProcessingMarker {
    blockedByBilling: boolean;
    thresholdState: BillingThresholdState | "blocked";
    blockReason: BillingBlockReason | null;
}

interface SessionBillingBlockedResult {
    billingBlocked: BillingBlockedPayload | null;
    blockedBySessionMarker: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

function toThresholdState(value: unknown): BillingThresholdState | null {
    return value === "active" ||
        value === "warning_80" ||
        value === "warning_100" ||
        value === "blocked"
        ? value
        : null;
}

function toBlockReason(value: unknown): BillingBlockReason | null {
    return value === "trial_hard_stop" ||
        value === "free_tier_hard_stop" ||
        value === "paid_plan_over_grace"
        ? value
        : null;
}

export function getSessionBillingProcessingMarker(
    rawReport: unknown,
): SessionBillingProcessingMarker | null {
    if (!isRecord(rawReport) || Array.isArray(rawReport)) {
        return null;
    }

    const billingProcessing = rawReport.billingProcessing;
    if (!isRecord(billingProcessing)) {
        return null;
    }

    const blockedByBilling = billingProcessing.blockedByBilling === true;
    if (!blockedByBilling) {
        return null;
    }

    return {
        blockedByBilling: true,
        thresholdState:
            toThresholdState(billingProcessing.thresholdState) ?? "blocked",
        blockReason: toBlockReason(billingProcessing.blockReason),
    };
}

// AIDEV-NOTE: Keep session billing block banner payload construction centralized across session surfaces.
export function getSessionBillingBlockedPayload(params: {
    rawReport: unknown;
    entitlement: BillingEntitlement | null | undefined;
}): SessionBillingBlockedResult {
    const marker = getSessionBillingProcessingMarker(params.rawReport);
    if (!marker) {
        return {
            billingBlocked: null,
            blockedBySessionMarker: false,
        };
    }

    return {
        blockedBySessionMarker: true,
        billingBlocked: {
            code: "BILLING_LLM_BLOCKED",
            message:
                "Session processing is paused while your plan's session limit is reached.",
            enforced: true,
            metricType: params.entitlement?.metricType ?? null,
            thresholdState: marker.thresholdState,
            blockReason: marker.blockReason,
            usedUnits: params.entitlement?.usedUnits ?? null,
            limitUnits: params.entitlement?.limitUnits ?? null,
        },
    };
}
